import BasicChart from "components/dashboard/charts/BasicChart";
import { T } from "services/lang/LanguageService";
import { configLineChartData } from "components/dashboard/charts/ChartConfig/configLineChartData";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { ChartData } from "components/dashboard/dataset-window/models/types";
import React, { useState, useEffect } from "react";
import { handleRowReorderEvt } from "utils/utils";
import { Dialog } from "primereact/dialog";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import { ITaskConfigConstantFieldProps } from "api/model/airflow-task/task-template/TaskTemplateInfo";

interface XYValueMatrix {
  x: number;
  y: number;
  [key: string]: number;
}

interface XYValueMatrixCustomConfig {
  xLabel: string;
  xUnit: string;
  yLabel: string;
  yUnit: string;
}

interface EditXYValueMatrixProps extends ITaskConfigConstantFieldProps<XYValueMatrixCustomConfig> {}

const initialXYValues = [{ x: 1, y: 1 }];

const colorPallet = {
  value: ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b"],
};

const selectedChartType = "LINE";

const EditXYValueMatrix = (props: EditXYValueMatrixProps) => {
  const xaxisName = props.fieldOptions?.customConfig?.xLabel || "x";
  const yaxisName = props.fieldOptions?.customConfig?.yLabel || "y";
  const xyValuesFromConfig = props.value || initialXYValues;
  const [XYValues, setXYValues] = useState<XYValueMatrix[]>(xyValuesFromConfig);
  const [editXYValueMatrixVisibility, setEditXYValueMatrixVisibility] = useState<boolean>(false);
  const [chartData, setChartData] = useState<ChartData[]>([]);

  useEffect(() => {
    setChartData(newChartData());
  }, [XYValues]);

  const newChartData = () => {
    const newChartDataObject: ChartData[] = [
      {
        id: "",
        unit:
          `${yaxisName} ${props.fieldOptions?.customConfig?.yUnit ? `(${props.fieldOptions.customConfig.yUnit})` : ""}` ||
          "",
        title: `${xaxisName} ${props.fieldOptions?.customConfig?.xUnit ? `(${props.fieldOptions.customConfig.xUnit})` : ""} /`,
        keyArr: XYValues.map(item => item.x),
        valueArr: XYValues.map(item => item.y),
      },
    ];
    return newChartDataObject;
  };

  const addXYValues = () => {
    setXYValues([...XYValues, { x: 0, y: 0 }]);
  };

  const handleValuesChange = (rowOptions: XYValueMatrix, value: number) => {
    setXYValues(prevState =>
      prevState.map((item, i) => (i === rowOptions.rowIndex ? { ...item, [rowOptions.field]: value } : item))
    );

    setChartData(newChartData());
  };

  const removeValues = (options: any) => {
    const updatedValues = XYValues.filter((value, i) => i !== options.rowIndex);
    setXYValues(updatedValues);
  };

  const onRowEditComplete = (e: any) => {
    const datasetsToUpdate = [...XYValues];
    const { newData, index } = e;
    delete newData.index;
    datasetsToUpdate[index] = e.data;
    setXYValues(datasetsToUpdate);
  };

  const handleTableChange = (e: any) => {
    handleRowReorderEvt(XYValues, setXYValues)(e);
    setChartData(newChartData());
  };

  const columnBodyInputTemplate = (rowData: XYValueMatrix, rowOptions: any) => {
    return (
      <InputNumber
        value={rowData[rowOptions.field]}
        style={{ padding: "4px", width: "50%" }}
        onValueChange={(e: any) => handleValuesChange(rowOptions, Number(e.target.value))}
        mode="decimal"
        minFractionDigits={2}
      />
    );
  };

  const removeButton = (options: any) => (
    <Button
      icon="pi pi-trash"
      className="const-del-btn  p-button-rounded p-button-text p-button-danger"
      onClick={() => removeValues(options)}
    />
  );

  const onSave = () => {
    props.onChange?.({ value: XYValues, property: props.property });
    setEditXYValueMatrixVisibility(false);
  };

  const onCancel = () => {
    setEditXYValueMatrixVisibility(false);
    setXYValues(xyValuesFromConfig);
  };

  return (
    <div>
      <div className="mb-2">
        <b>{props.label}</b>
        <div className="inline-block">
          <CopyTextWithButton text="" copyableText={props.label} />
        </div>
      </div>
      <Button
        className="p-button-outlined p-button-secondary"
        label={T("action.edit")}
        onClick={() => setEditXYValueMatrixVisibility(true)}
        style={{ width: "100%" }}
        aria-controls="popup_menu"
        aria-haspopup
      />
      <Dialog
        style={{ width: "min(100vw, 800px)", maxHeight: "600px" }}
        header={T("action.edit")}
        visible={!!editXYValueMatrixVisibility}
        onHide={onCancel}
        resizable
      >
        <DataTable
          value={XYValues}
          onRowEditComplete={onRowEditComplete}
          editMode="row"
          reorderableRows
          onRowReorder={handleTableChange}
          style={{ paddingBottom: "5px" }}
        >
          <Column rowReorder style={{ width: "3%", padding: "1px" }} />
          <Column
            field="x"
            header={
              <div className="flex align-items-center">
                <div>
                  {xaxisName} {T("word.value")}{" "}
                  {props.fieldOptions?.customConfig?.xUnit
                    ? `(${props.fieldOptions.customConfig.xUnit})`
                    : ""}
                </div>
              </div>
            }
            style={{ padding: "1px" }}
            body={(rowData, options) => columnBodyInputTemplate(rowData, options)}
          />
          <Column
            field="y"
            header={
              <div className="flex align-items-center">
                <div>
                  {yaxisName} {T("word.value")}{" "}
                  {props.fieldOptions?.customConfig?.yUnit
                    ? `(${props.fieldOptions.customConfig.yUnit})`
                    : ""}
                </div>
              </div>
            }
            style={{ padding: "1px" }}
            body={(rowData, options) => columnBodyInputTemplate(rowData, options)}
          />
          <Column
            style={{ width: "40%", textAlign: "right", padding: "1px" }}
            body={(rowData, options) => removeButton(options)}
          />
        </DataTable>
        <div className="flex justify-content-between">
          <div>
            <Button
              className="p-button-outlined p-button-secondary"
              label={T("word.add")}
              onClick={addXYValues}
            />
          </div>
          <div className="flex gap-2">
            <Button className="p-button-success" label={T("action.save")} onClick={onSave} />
            <Button className="p-button-danger" label={T("word.cancel")} onClick={onCancel} />
          </div>
        </div>
        <BasicChart
          plotData={configLineChartData(chartData, colorPallet.value)}
          chartProps={{
            title: selectedChartType,
            identifiers: [],
            hideRefresh: true,
            height: 300,
            selectedChartType,
          }}
          data={[]}
          selectedDatasets={[]}
        />
      </Dialog>
    </div>
  );
};

export default EditXYValueMatrix;
